'use client';
import React, { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import MobileNav from '@/components/MobileNav';
import { useAuth } from '@/contexts/AuthContext';
import { LogOut } from 'lucide-react';

const navLinks = [{ label: 'Courses', href: '/courses', key: 'nav-courses' }];

export default function AppHeader() {
  const { user, logout, isLoading } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    await logout();
  };

  const initial = user?.fullname ? user.fullname.trim().charAt(0).toUpperCase() : '?';

  return (
    <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-sm border-b border-brand-border">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 xl:px-16 h-16 flex items-center justify-between">
        <Link href="/" className="flex items-center gap-2.5 group">
          <AppLogo size={32} />
          <span className="font-semibold text-brand-text text-lg tracking-tight">
            Spreadd<span className="text-brand-primary font-light">LMS</span>
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-1">
          {navLinks?.map((link) => (
            <Link
              key={link?.key}
              href={link?.href}
              className="px-4 py-2 rounded-lg text-sm font-medium text-brand-text-muted hover:text-brand-text-dark hover:bg-brand-bg transition-all duration-150"
            >
              {link?.label}
            </Link>
          ))}
        </nav>

        {/* Desktop User Menu */}
        <div className="hidden md:flex items-center gap-3">
          {/* <Link href="/completion-certificate-screen" className="btn-ghost text-xs">
            Certificate
          </Link>
          <Link href="/day-overview" className="btn-primary text-xs px-4 py-2">
            Continue Today →
          </Link> */}
          {isLoading ? (
            <div className="w-9 h-9 rounded-full bg-brand-bg border border-brand-border animate-pulse" />
          ) : user ? (
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="w-9 h-9 rounded-full bg-brand-bg border border-brand-border text-brand-text-muted hover:bg-brand-border transition-colors flex items-center justify-center text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-brand-primary"
              >
                {initial}
              </button>

              {dropdownOpen && (
                <div className="absolute right-0 mt-2 w-56 rounded-xl border border-brand-border bg-white shadow-lg py-1.5 z-50">
                  <div className="px-4 py-2 text-xs border-b border-brand-border">
                    <p className="text-brand-text-muted">Signed in as</p>
                    <p className="font-medium text-brand-text truncate mt-0.5">{user.fullname}</p>
                  </div>
                  <button
                    onClick={handleLogout}
                    disabled={isLoggingOut}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-brand-error hover:bg-brand-error/5 transition-colors text-left font-medium disabled:opacity-50"
                  >
                    <LogOut size={16} />
                    {isLoggingOut ? 'Signing out...' : 'Sign out'}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link
              href="/auth/login"
              className="px-4 py-2 rounded-lg bg-brand-primary text-white text-sm font-semibold hover:opacity-90 transition-opacity"
            >
              Sign in
            </Link>
          )}
        </div>

        {/* Mobile nav */}
        <MobileNav links={navLinks} />
      </div>
    </header>
  );
}
